'use strict';
/**
 * AnalyticsEventBinder.js
 * 
 * The AnalyticsEventBinder class will bind click events to elements that can make analytics.track. 
 * Configuration driven using the ANALYTICS_CONFIG object.
 * On DOMContentLoaded, appends click listeners to Anchor elements with the 'data-analytics' attribute, 
 * On any MutaionObserver DOM changes, for newly added DOM elements, does the same as above.
 *
 * Dependencies:
 *  - No external libraries.
 *  - Availability of window.analytics and window.disw.locales.getCurrentLocaleCode()
 * 
 * Effects: 
 *  Will append the following to each valid DOM element:
 *  - property  : 'analyticsParsedData'       : The parsed JSON data from the 'data-analytics' attribute.
 *  - attribute : 'data-analytics-processed'  : Indicates that the element has been event binded.
 *
 * 
 * Key reasons for this implementation:
 * 
 * 1. Avoiding Hydration Issues: Integrating analytics directly into React components 
 *    like digitalExp-lib/UIButton.js can lead to hydration needs as the need to add 
 *    onClick interation will need conversion from reactStatic to reactString
 * 
 * 2. Possibility of Centralizing Analytics Logic: By managing all analytics bindings within
 *    this class, we can centralize all analytics.track calls. The ANALYTICS constant can be
 *    enhanced to include all EVENT names and their associated payloads.
 * 
 */

const CONTENT_TYPES = {
  PDF: 'application/pdf',
};

/**
 * Analytics events name constants.
 * Keys are used within the ANALYTICS_CONFIG object.
 */
const ANALYTICS_EVENTS = {
  ASSET_ACCESSED: 'Asset Accessed',
};

/**
 * Object containing names that map to filter functions. 
 * @typedef {Object} FILTER_NAMES
 */
const FILTER_NAMES = {
  FILTER_EVENT_SENT_FOR_USING_CONTENT_TYPE: 'filterEventSentForUsingContentType',
};

/**
 * Constants for analytics events.
 * 
 * @todo Future enhancements to consider:
 *   - enablement         : env specific [ NOPE, ALL, PROD, QA, DEV ]
 *   - eventSourcePage    : Indicate pages from where the event is sent.
 *   - eventSentTo        : Destination. Good for documenting ???
 *   - eventSentVia       : Specify the lib for sending the event (e.g., Google Tag Manager).
 *   - eventSentByEle     : Identify UI components that trigger the event.
 *   - DOM_CONFIG         : Config for DOM elements to be processed.
 * 
 *      Example:
 *      /* 
 *       * Possible config to remove eleAnalyticsAttrName, selector
 *       * Individual EVENTS inherit by default, Overrideable.
 *       *
 *      DOM_CONFIG: {
 *        eleAttrName: 'data-analytics',
 *        eleSource: 'a'
 *        eleSelector: 'a[data-analytics]',
 *      }
 */
export const ANALYTICS_CONFIG = {
  TRACK_EVENTS: {
    ASSET_ACCESSED: {
      isEnabled: true,
      eventName: ANALYTICS_EVENTS.ASSET_ACCESSED,
      eventSentFor: [CONTENT_TYPES.PDF], // Could some events not have this field? If so, how to handle?
      filterFnName: FILTER_NAMES.FILTER_EVENT_SENT_FOR_USING_CONTENT_TYPE,
      payloadStructure: {
        payload: {
          // Final payload to be sent to analytics.track and its data type.
          asset_id: 'String',
          asset_name: 'String',
          content_type: 'String',
          language: 'String',
        },
        defaults: {
          // any default values for payload keys,
          // takes first priority and overrides any conditions from mapToDomValues
          // Eg entry: language: 'en-US',
        },
        mapToDomValues: {
          // mapping of payload keys values in the DOM data attributes.
          // easy way to bridge data created from other mappers that creates the data-* values in DOM
          asset_id: 'id',
          asset_name: 'name',
          content_type: 'contentType',
        },
        contextValues: {
          language: () => window?.disw?.locales?.getCurrentLocaleCode(),
          // ...other context values...
        },
    },
    },
  },
};


export const Helpers = {
  /**
   * Extends each event in TRACK_EVENTS of the given object with additional properties.
   * If TRACK_EVENTS is missing,the original object is returned unmodified.
   *
   * @param {Object} obj - The object containing TRACK_EVENTS.
   * @param {Object} extaPropsObj - Extra properties to add to each TRACK_EVENTS event.
   * @returns {Object} The object with extended events or the original object if TRACK_EVENTS is absent.
   *
   * @example
   * const baseObj = { TRACK_EVENTS: { EVENT1: { id: 1 } } };
   * const extraProps = { extraKey: 'extraValue' };
   * const modifiedObj = Helpers.addExtraPropsToObj(baseObj, extraProps);
   * // modifiedObj.TRACK_EVENTS.EVENT1 has extraKey added.
   */
  addExtraPropsToObj: (obj = {}, extaPropsObj = {}) => {
    if(!obj.TRACK_EVENTS) {
      console.error('Invalid object. No TRACK_EVENTS property found to add extraPops.', obj, extaPropsObj);
      return obj;
    }
    for (const eventKey in obj.TRACK_EVENTS) {
      const event = obj.TRACK_EVENTS[eventKey];
      Object.assign(event, extaPropsObj);
    }
    return obj;
  }
};

export class AnalyticsEventBinder {
  static extraPropsToAnalyticsConfig = {
    inValidConfigProperties: [],
    isInValidConfig: false,
  }

  constructor(ANALYTICS = ANALYTICS_CONFIG) {
    this.ANALYTICS = this.validateConfig(ANALYTICS);
    this.FILTER_NAMES = FILTER_NAMES;

    this.eleAnalyticsAttrName = 'data-analytics';
    this.analyticsProcessedAttrName = 'data-analytics-processed';
    this.selector = 'a[data-analytics]:not([data-analytics-processed])';

    this.elements = {
      eleWithAnalyticsAttr: [],
      eleByTrackEvents: {},
    };


    this.filterLogics = {
      [this.FILTER_NAMES.FILTER_EVENT_SENT_FOR_USING_CONTENT_TYPE]:
        (ele, eventConfig) => eventConfig.eventSentFor.includes(ele.analyticsParsedData?.contentType),
    };
  }

  /**
   * Validates the analytics configuration object. 
   * Adds extraPropsToAnalyticsConfig values to each event.
   * Only logs the error of an event to console, permitting other events to be processed.
   * @param {Object} analytics - The analytics configuration object.
   * @returns {Object} - A modified copy of the validated analytics configuration.
   */
  validateConfig(analytics) {
    const copyWithExtraProps = Helpers.addExtraPropsToObj(analytics, AnalyticsEventBinder.extraPropsToAnalyticsConfig);

    for (const eventKey in copyWithExtraProps.TRACK_EVENTS) {
      const event = copyWithExtraProps.TRACK_EVENTS[eventKey];

      // Validate isEnabled
      if (typeof event.isEnabled !== 'boolean') {
        event.isInValidConfig = true;
        event.inValidConfigProperties = event.inValidConfigProperties.concat('isEnabled')
      }

      // Validate eventSentFor
      if (!Array.isArray(event.eventSentFor)) {
        event.isInValidConfig = true;
        event.inValidConfigProperties = event.inValidConfigProperties.concat('eventSentFor')
      }

      // Validate eventName
      if (typeof event.eventName !== 'string' || event.eventName !== ANALYTICS_EVENTS[eventKey]) {
        event.isInValidConfig = true;
        event.inValidConfigProperties = event.inValidConfigProperties.concat('eventName')
      }

      // Validate filterFnName
      if (typeof event.filterFnName !== 'string' || !Object.values(FILTER_NAMES).includes(event.filterFnName)) {
        event.isInValidConfig = true;
        event.inValidConfigProperties = event.inValidConfigProperties.concat('filterFnName')
      }

      // Validate payloadStructure
      if (typeof event.payloadStructure !== 'object' || typeof event.payloadStructure.payload !== 'object') {
        event.isInValidConfig = true;
        event.inValidConfigProperties = event.inValidConfigProperties.concat('payloadStructure')
      }

      // Gracefully log invalid config
      if (event.isInValidConfig) {
        console.error('Invalid Analytics config:', event);
      }
    }

    return copyWithExtraProps;
  }

  
  /**
   * Retrieves elements with the specified analytics attribute.
   * Sets/Updates the elements.eleWithAnalyticsAttr property.
   * @param {string} selector - The CSS selector to match elements.
   * @param {Array<MutationRecord>} [mutations] - Optional array of MutationRecord objects.
   * @returns {Object} - The current instance of the class.
   */
  getElementsWithAnalyticsAttr(selector, mutations) {
    if(mutations) {
      const validMutations = mutations.filter(mutation => mutation.addedNodes.length > 0);
      const newNodes = validMutations.flatMap(mutation => Array.from(mutation.addedNodes));
      const newEleWithAnalyticsAttr = newNodes.filter(newNode => newNode.matches && newNode.matches(selector));
  
      this.elements.eleWithAnalyticsAttr.push(...newEleWithAnalyticsAttr);
    } else {
      this.elements.eleWithAnalyticsAttr = [...document.querySelectorAll(selector)];
    }
    return this;
  }

  /**
   * Parses the element's 'data-analytics' attribute value, and
   * Assigns it to the 'analyticsParsedData' property of each element.
   * @example 
   * // Example of a 'data-analytics' attribute in DOM:
   * // <a href="#" data-analytics="{&quot;name&quot;:&quot;UCA-V1-1-Czech&quot;,&quot;id&quot
   * ;:&quot;20COc8XcWoOg70ugcgaldJ&quot;,&quot;contentType&quot;:&quot;application/pdf&quot;}"
   * @returns {Object} The current instance of the analyticsEventBinder.
   */
  parseElementsAnalyticsData() {
    this.elements.eleWithAnalyticsAttr = this.elements.eleWithAnalyticsAttr.map(ele => {
      if(ele.analyticsParsedData) return ele; // Not a new element
      try {
        ele.analyticsParsedData = JSON.parse(ele.getAttribute(this.eleAnalyticsAttrName));
        return ele;
      } catch (error) {
        console.error('Error parsing data-analytics for ele:', ele);
        console.error('Error parsing JSON:', error);
        return null;
      }
    }).filter(ele => ele !== null);
    return this;
  }

  /**
   * Filters the elements from 'eleWithAnalyticsAttr' and adds them 'eleByTrackEvents<eventName>'.
   * Uses <eventName>.filterFnName to determine the filter function to use.
   * Uses <eventName>.isEnabled to determine if the event is enabled.
   * Uses <eventName>.isInValidConfig to determine if the event config is valid.
   * @returns {Object} The current instance of the analyticsEventBinder.
   */
  filterElementsEligibleForAnalytics() {
    const { eleWithAnalyticsAttr, eleByTrackEvents } = this.elements;
    Object.keys(this.ANALYTICS.TRACK_EVENTS).forEach(event => {
      const eventConfig = this.ANALYTICS.TRACK_EVENTS[event];

      if (!eventConfig.isEnabled || eventConfig.isInValidConfig) return;

      const filterFn = this.filterLogics[eventConfig.filterFnName];
      if (!filterFn) return;

      eleByTrackEvents[event] = eleWithAnalyticsAttr.filter(ele =>
        filterFn(ele, eventConfig)
      );
    });
    return this;
  }

  /**
   * Attaches a click event listener to all the filtered elements.
   * Builds the analytics payload data from the element's "analyticsParsedData" property, and
   * Sends an analytics event when the element is clicked.
   * @returns {Object} The current instance of the analyticsEventBinder.
   */
  attachClickEventForAnalytics() {
    Object.entries(this.elements.eleByTrackEvents).forEach(([eventType, eles]) => {
      eles.forEach(ele => {
        if (ele.getAttribute(this.analyticsProcessedAttrName)) return; // Already processed

        // Attaching click handler does not override existing onClick or handlers
        ele.addEventListener('click', () => {
          const { eventName, payloadStructure } = this.ANALYTICS.TRACK_EVENTS[eventType];
          const payload = this.buildAnalyticsPayloadData(ele.analyticsParsedData, payloadStructure);
          this.sendAnalyticsEvent(eventName, payload);
        }); // Attach the click handler

        ele.setAttribute(this.analyticsProcessedAttrName, true); // Mark as processed
      });
    });
    return this;
  }


  /**
   * Builds the analytics payload data based on the provided analyticsParsedData and eventConfig.
   * @param {Object} analyticsParsedData - The parsed analytics data.
   * @param {Object} eventConfig - The event configuration object.
   * @param {Object} eventConfig.payloadStructure - The structure of the payload.
   * @param {Object} eventConfig.payloadStructure.payload - The payload structure.
   * @param {Object} eventConfig.payloadStructure.mapToDomValues - The mapping of payload keys to DOM values.
   * @param {Object} eventConfig.payloadStructure.defaults - The default values for payload keys.
   * @param {Object} eventConfig.payloadStructure.contextValues - The context values for payload keys.
   * @returns {Object} - The final analytics payload.
   */
  buildAnalyticsPayloadData(analyticsParsedData = {}, payloadStructure) {
    // Build finalPayload using the configured structure
    let finalPayload = { ...payloadStructure.payload };
    Object.keys(payloadStructure.payload).forEach(key => {
        // Map to DOM values or use default if available
        const domKey = payloadStructure.mapToDomValues[key];
        finalPayload[key] = payloadStructure.defaults[key] || // 1st priority
                            analyticsParsedData[domKey] || // 2nd priority
                            payloadStructure.payload[key];
        //console.log('key', key, 'domKey', domKey, 'finalPayload[key]', [key], 'finalPayload', finalPayload)
    });
    // Include context values
    if (payloadStructure.contextValues) {      
      Object.entries(payloadStructure.contextValues).forEach(([key, value]) => {
        if(payloadStructure.defaults[key]) return; // 1st priority assigned already
        finalPayload[key] = typeof value === 'function' ? value() : value;
      });
    }
    return finalPayload;
  }


  /**
   * Sends an analytics event.
   * @param {string} eventName - The name of the event.
   * @param {object} payload - The data payload associated with the event.
   * @returns {object} - The instance of the analyticsEventBinder.
   */
  sendAnalyticsEvent(eventName, payload) {
    window?.analytics?.track(eventName, payload);
    return this;
  }

  /**
   * Observes DOM changes within the document body and triggers a callback function when changes occur.
   * @param {string} selector - The CSS selector to match the elements to be observed.
   * @returns {Object} - The current instance of the object.
   */
  observeDOMChanges(selector) {
    const observer = new MutationObserver(mutations => {
      this.processDOMElements(selector, mutations);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return this;
  }

  /**
   * Processes DOM elements based on the provided selector and mutations.
   * Main entry point for analytics event binding.
   * @param {string} selector - The CSS selector to select the DOM elements.
   * @param {Array<MutationRecord>|null} mutations - The array of MutationRecord objects representing DOM mutations or null
   * @returns {this} - The current instance of the analyticsEventBinder.
   */
  processDOMElements(selector, mutations) {
    this.getElementsWithAnalyticsAttr(selector, mutations)
      .parseElementsAnalyticsData()
      .filterElementsEligibleForAnalytics()
      .attachClickEventForAnalytics();
    return this;
  }

  /**
   * Initializes the analytics event binder.
   * @returns {Object} The instance of the analytics event binder.
   */
  init() {
    document.addEventListener('DOMContentLoaded', () => {
      this.processDOMElements(this.selector, null);
      this.observeDOMChanges(this.selector);
    });
    return this;
  }
}

// Self-initialize
export const AnalyticsEventBinderInstance = new AnalyticsEventBinder(ANALYTICS_CONFIG).init();

